"use strict";
(() => {
  const VERSION = "v2";
  const MAX_ITEMS = 100;
  const CHUNK_SIZE = 20;

  const config = window.cdxWishlistConfig || {};
  const customerId = config.customerId ? String(config.customerId) : null;
  const scope = customerId ? `cust:${customerId}` : "guest";
  const storageKey = `cdxWishlist:${VERSION}:${scope}`;
  const guestKey = `cdxWishlist:${VERSION}:guest`;

  const readRaw = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch (error) {
      return null;
    }
  };

  const normalizeEntries = (value) => {
    if (!Array.isArray(value)) return [];
    return value
      .map((entry) => {
        if (typeof entry === "string" || typeof entry === "number") {
          return { id: String(entry), t: Date.now() };
        }
        if (entry && entry.id) {
          return { id: String(entry.id), t: entry.t || Date.now() };
        }
        return null;
      })
      .filter(Boolean);
  };

  const migrate = () => {
    ["guest", customerId ? `cust:${customerId}` : null].filter(Boolean).forEach((s) => {
      const v1Key = `cdxWishlist:v1:${s}`;
      const v2Key = `cdxWishlist:${VERSION}:${s}`;
      const v1 = readRaw(v1Key);
      if (Array.isArray(v1) && !readRaw(v2Key)) {
        try {
          localStorage.setItem(v2Key, JSON.stringify(normalizeEntries(v1)));
        } catch (error) {}
      }
      try {
        localStorage.removeItem(v1Key);
      } catch (error) {}
    });
  };
  migrate();

  const read = (key) => normalizeEntries(readRaw(key));

  // Parsed list of this page, so the toggles on a collection page don't each parse localStorage
  let cachedIds = null;
  const ownIds = () => {
    if (!cachedIds) cachedIds = read(storageKey).map((e) => e.id);
    return cachedIds;
  };

  const write = (entries) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(entries.slice(0, MAX_ITEMS)));
    } catch (error) {}
    cachedIds = null;
    window.dispatchEvent(new CustomEvent("cdx:wishlist:change", { detail: { ids: ownIds().slice() } }));
  };

  // Changes in another tab
  window.addEventListener("storage", (event) => {
    if (event.key !== storageKey) return;
    cachedIds = null;
    window.dispatchEvent(new CustomEvent("cdx:wishlist:change", { detail: { ids: ownIds().slice() } }));
  });

  if (customerId) {
    const guestEntries = read(guestKey);
    if (guestEntries.length) {
      const own = read(storageKey);
      const ownIds = own.map((e) => e.id);
      const merged = [...own, ...guestEntries.filter((e) => !ownIds.includes(e.id))];
      try {
        localStorage.removeItem(guestKey);
      } catch (error) {}
      try {
        localStorage.setItem(storageKey, JSON.stringify(merged.slice(0, MAX_ITEMS)));
      } catch (error) {}
    }
  }

  const CdxWishlist = {
    getEntries() {
      return read(storageKey);
    },
    getIds() {
      return ownIds().slice();
    },
    has(id) {
      return ownIds().includes(String(id));
    },
    add(id) {
      id = String(id);
      const entries = read(storageKey);
      if (!entries.some((e) => e.id === id)) {
        entries.unshift({ id, t: Date.now() });
        write(entries);
      }
    },
    remove(id) {
      id = String(id);
      write(read(storageKey).filter((e) => e.id !== id));
    },
    toggle(id) {
      if (this.has(id)) {
        this.remove(id);
        return false;
      }
      this.add(id);
      return true;
    },
  };

  window.CdxWishlist = CdxWishlist;

  if (!customElements.get("cdx-wishlist-toggle")) {
    class CdxWishlistToggle extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector("button");
        this.label = this.querySelector(".cdx-wl-label");
        this.productId = String(this.dataset.productId || "");
        if (!this.productId || !this.button) return;

        this.onClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          CdxWishlist.toggle(this.productId);
        };
        this.onChange = () => this.sync();

        this.button.addEventListener("click", this.onClick);
        window.addEventListener("cdx:wishlist:change", this.onChange);
        this.sync();
      }

      disconnectedCallback() {
        this.button?.removeEventListener("click", this.onClick);
        window.removeEventListener("cdx:wishlist:change", this.onChange);
      }

      sync() {
        const active = CdxWishlist.has(this.productId);
        this.classList.toggle("cdx-wishlist-active", active);
        this.button.setAttribute("aria-pressed", active ? "true" : "false");
        this.button.setAttribute(
          "aria-label",
          active ? "Von der Wunschliste entfernen" : "Zur Wunschliste hinzufügen"
        );
        if (this.label) {
          const text = active ? this.label.dataset.labelActive : this.label.dataset.labelAdd;
          if (text) this.label.textContent = text;
        }
      }
    }
    customElements.define("cdx-wishlist-toggle", CdxWishlistToggle);
  }

  if (!customElements.get("cdx-wishlist-count")) {
    class CdxWishlistCount extends HTMLElement {
      connectedCallback() {
        this.onChange = () => this.sync();
        window.addEventListener("cdx:wishlist:change", this.onChange);
        this.sync();
      }

      disconnectedCallback() {
        window.removeEventListener("cdx:wishlist:change", this.onChange);
      }

      sync() {
        const count = CdxWishlist.getIds().length;
        this.textContent = count > 99 ? "99+" : String(count);
        this.hidden = count === 0;
      }
    }
    customElements.define("cdx-wishlist-count", CdxWishlistCount);
  }

  if (!customElements.get("cdx-wishlist-grid")) {
    class CdxWishlistGrid extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector("[data-cdx-wishlist-grid]");
        this.emptyEl = this.querySelector("[data-cdx-wishlist-empty]");
        this.loadingEl = this.querySelector("[data-cdx-wishlist-loading]");
        if (!this.grid) return;

        this.onChange = (event) => this.applyRemovals(event.detail?.ids || CdxWishlist.getIds());
        window.addEventListener("cdx:wishlist:change", this.onChange);

        if (!this.fetched) {
          this.fetched = true;
          this.load();
        }
      }

      disconnectedCallback() {
        window.removeEventListener("cdx:wishlist:change", this.onChange);
      }

      async load() {
        const ids = CdxWishlist.getIds();

        if (!ids.length) {
          this.finish();
          return;
        }

        const chunks = [];
        for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
          chunks.push(ids.slice(i, i + CHUNK_SIZE));
        }

        for (const chunk of chunks) {
          try {
            const query = chunk.map((id) => `id:${id}`).join(" OR ");
            const url = `${window.routes.search_url}?section_id=${this.dataset.sectionId}&type=product&q=${encodeURIComponent(query)}`;
            const response = await fetch(url);
            if (!response.ok) continue;
            const content = await response.text();
            const html = document.createElement("div");
            html.innerHTML = content;
            const items = html.querySelectorAll("[data-cdx-wishlist-grid] > .grid-item");
            items.forEach((item) => this.grid.appendChild(item));
          } catch (error) {}
        }

        this.reorderUnpurchasable();
        this.finish();
      }

      reorderUnpurchasable() {
        this.grid.querySelectorAll(".grid-item").forEach((item) => {
          if (item.querySelector(".cdx-atc-button--details")) {
            this.grid.appendChild(item);
          }
        });
      }


      finish() {
        if (this.loadingEl) this.loadingEl.hidden = true;
        const hasItems = this.grid.children.length > 0;
        this.grid.hidden = !hasItems;
        if (this.emptyEl) this.emptyEl.hidden = hasItems;
      }

      applyRemovals(ids) {
        if (this.loadingEl && !this.loadingEl.hidden) return;
        this.grid.querySelectorAll("product-card[data-product-id]").forEach((card) => {
          if (!ids.includes(String(card.dataset.productId))) {
            card.closest(".grid-item")?.remove();
          }
        });
        this.finish();
      }

    }
    customElements.define("cdx-wishlist-grid", CdxWishlistGrid);
  }
})();
